import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Header from '../components/Secciones/Header';
import Footer from '../components/Secciones/Footer';
import { UserAuthContext } from '../UserAuthContext';
import { addToGuestCart } from '../utils/guestCart';
import { getProductImageUrl, useProductImageFallback } from '../utils/productImages';
import { getProductPath } from '../utils/productUrls';
import NotFoundImage from '../assets/images/NotFound.png';
import './SearchResults.css';

const API_BASE = 'https://valleyapi-dr9y.onrender.com';
const PRODUCTS_PER_PAGE = 12;
const MULTI_FILTER_PAGE_SIZE = 100;

const uniqueValues = (values) => [...new Set(values.filter(Boolean))];

const parseResponse = (data) => {
  if (Array.isArray(data)) {
    return {
      products: data.filter((product) => product.published !== 'No'),
      totalResults: data.length,
      totalPages: 1,
      currentPage: 1,
      uniqueBrands: [],
      uniqueCategories: []
    };
  }

  return {
    products: Array.isArray(data?.products) ? data.products : [],
    totalResults: Number(data?.totalResults) || data?.products?.length || 0,
    totalPages: Number(data?.totalPages) || 1,
    currentPage: Number(data?.currentPage) || 1,
    uniqueBrands: Array.isArray(data?.uniqueBrands) ? data.uniqueBrands : [],
    uniqueCategories: Array.isArray(data?.uniqueCategories) ? data.uniqueCategories : []
  };
};

const formatPrice = (price) => {
  const parsed = Number.parseFloat(price);
  if (!Number.isFinite(parsed) || parsed <= 0) return 'Request pricing';
  return parsed.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const truncateText = (text, maxLength) => {
  if (!text) return 'N/A';
  return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
};

const sortProducts = (products, sort) => {
  const sorted = [...products];
  const compareText = (a, b, key) => String(a[key] || '').localeCompare(String(b[key] || ''));
  const compareNumber = (a, b, key) => (Number(a[key]) || 0) - (Number(b[key]) || 0);
  const compareRank = (a, b, direction = 1) => {
    const aRank = Number(a.product_rank);
    const bRank = Number(b.product_rank);
    const aHasRank = Number.isInteger(aRank) && aRank > 0;
    const bHasRank = Number.isInteger(bRank) && bRank > 0;
    if (aHasRank !== bHasRank) return aHasRank ? -1 : 1;
    if (!aHasRank) return 0;
    return (aRank - bRank) * direction;
  };

  switch (sort) {
    case 'price_asc': return sorted.sort((a, b) => compareNumber(a, b, 'product_price'));
    case 'price_desc': return sorted.sort((a, b) => compareNumber(b, a, 'product_price'));
    case 'stock_asc': return sorted.sort((a, b) => compareNumber(a, b, 'product_stock'));
    case 'stock_desc': return sorted.sort((a, b) => compareNumber(b, a, 'product_stock'));
    case 'brand_asc': return sorted.sort((a, b) => compareText(a, b, 'product_brand'));
    case 'brand_desc': return sorted.sort((a, b) => compareText(b, a, 'product_brand'));
    case 'name_asc': return sorted.sort((a, b) => compareText(a, b, 'product_name'));
    case 'name_desc': return sorted.sort((a, b) => compareText(b, a, 'product_name'));
    case 'rank_asc': return sorted.sort((a, b) => compareRank(a, b));
    case 'rank_desc': return sorted.sort((a, b) => compareRank(a, b, -1));
    default: return sorted;
  }
};

export default function SearchResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserAuthContext);
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const searchTerm = params.get('q') || '*';
  const selectedBrands = useMemo(() => uniqueValues(params.getAll('brand')), [params]);
  const selectedCategories = useMemo(() => uniqueValues(params.getAll('category')), [params]);
  const selectedAvailability = params.get('availability') || (params.get('inStock') === 'true' ? 'in_stock' : '');
  const selectedSort = params.get('sort') || 'default';
  const currentPage = Math.max(1, Number.parseInt(params.get('page'), 10) || 1);
  const appliedMinPrice = params.get('minPrice') || '';
  const appliedMaxPrice = params.get('maxPrice') || '';

  const [results, setResults] = useState([]);
  const [facets, setFacets] = useState({ brands: [], categories: [] });
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [facetsLoading, setFacetsLoading] = useState(true);
  const [error, setError] = useState('');
  const [quantities, setQuantities] = useState({});
  const [adding, setAdding] = useState({});
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [minPrice, setMinPrice] = useState(appliedMinPrice);
  const [maxPrice, setMaxPrice] = useState(appliedMaxPrice);

  useEffect(() => {
    setMinPrice(appliedMinPrice);
    setMaxPrice(appliedMaxPrice);
  }, [appliedMinPrice, appliedMaxPrice]);

  const updateParams = useCallback((update) => {
    const next = new URLSearchParams(location.search);
    next.delete('inStock');
    next.set('q', next.get('q') || '*');

    Object.entries(update).forEach(([key, value]) => {
      next.delete(key);
      if (Array.isArray(value)) {
        value.filter(Boolean).forEach((item) => next.append(key, item));
      } else if (value !== '' && value !== null && value !== undefined && value !== false) {
        next.set(key, String(value));
      }
    });

    if (!Object.prototype.hasOwnProperty.call(update, 'page')) next.set('page', '1');
    navigate(`/search?${next.toString()}`, {
      state: { preserveScroll: true }
    });
  }, [location.search, navigate]);

  const getPageUrl = useCallback((page) => {
    const next = new URLSearchParams(location.search);
    next.set('q', next.get('q') || '*');
    next.set('page', String(page));
    return `/search?${next.toString()}`;
  }, [location.search]);

  useEffect(() => {
    const controller = new AbortController();

    const buildFacetUrl = ({ brand, category } = {}) => {
      const url = new URL(`${API_BASE}/api/products/search`);
      url.searchParams.set('q', searchTerm);
      url.searchParams.set('page', '1');
      url.searchParams.set('limit', '1');
      if (brand) url.searchParams.set('brand', brand);
      if (category) url.searchParams.set('category', category);
      if (selectedAvailability) url.searchParams.set('availability', selectedAvailability);
      if (appliedMinPrice) url.searchParams.set('minPrice', appliedMinPrice);
      if (appliedMaxPrice) url.searchParams.set('maxPrice', appliedMaxPrice);
      return url;
    };

    const loadFacetVariant = async (filters) => {
      const response = await fetch(buildFacetUrl(filters), { signal: controller.signal });
      if (!response.ok) throw new Error(`Facet request failed: ${response.status}`);
      return parseResponse(await response.json());
    };

    async function loadFacets() {
      setFacetsLoading(true);
      try {
        if (!selectedBrands.length && !selectedCategories.length) {
          const parsed = await loadFacetVariant();
          setFacets({
            brands: uniqueValues(parsed.uniqueBrands).sort(),
            categories: uniqueValues(parsed.uniqueCategories).sort()
          });
          return;
        }

        // Each facet is constrained by selections from the other dimension.
        // Multiple values within one dimension use OR semantics, matching the
        // way product variants are merged below.
        const brandFilters = selectedCategories.length ? selectedCategories : [null];
        const categoryFilters = selectedBrands.length ? selectedBrands : [null];
        const [brandResponses, categoryResponses] = await Promise.all([
          Promise.all(brandFilters.map((category) => loadFacetVariant({ category }))),
          Promise.all(categoryFilters.map((brand) => loadFacetVariant({ brand })))
        ]);

        setFacets({
          brands: uniqueValues(brandResponses.flatMap((response) => response.uniqueBrands)).sort(),
          categories: uniqueValues(categoryResponses.flatMap((response) => response.uniqueCategories)).sort()
        });
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          console.error('Unable to load search facets:', requestError);
          setFacets({ brands: [], categories: [] });
        }
      } finally {
        if (!controller.signal.aborted) setFacetsLoading(false);
      }
    }

    loadFacets();
    return () => controller.abort();
  }, [
    searchTerm,
    selectedAvailability,
    appliedMinPrice,
    appliedMaxPrice,
    selectedBrands,
    selectedCategories
  ]);

  useEffect(() => {
    const controller = new AbortController();

    const buildApiUrl = ({ brand, category, page, limit }) => {
      const url = new URL(`${API_BASE}/api/products/search`);
      url.searchParams.set('q', searchTerm);
      url.searchParams.set('page', String(page));
      url.searchParams.set('limit', String(limit));
      if (brand) url.searchParams.set('brand', brand);
      if (category) url.searchParams.set('category', category);
      if (selectedAvailability) url.searchParams.set('availability', selectedAvailability);
      if (appliedMinPrice) url.searchParams.set('minPrice', appliedMinPrice);
      if (appliedMaxPrice) url.searchParams.set('maxPrice', appliedMaxPrice);
      if (selectedSort !== 'default') url.searchParams.set('sort', selectedSort);
      return url;
    };

    const loadOnePage = async (options) => {
      const response = await fetch(buildApiUrl(options), { signal: controller.signal });
      if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
      return parseResponse(await response.json());
    };

    const loadVariant = async (brand, category) => {
      const first = await loadOnePage({
        brand,
        category,
        page: 1,
        limit: MULTI_FILTER_PAGE_SIZE
      });

      if (first.totalPages <= 1) return first.products;

      const remainingPages = Array.from(
        { length: first.totalPages - 1 },
        (_, index) => index + 2
      );
      const remaining = await Promise.all(
        remainingPages.map((page) => loadOnePage({
          brand,
          category,
          page,
          limit: MULTI_FILTER_PAGE_SIZE
        }))
      );
      return [...first.products, ...remaining.flatMap((response) => response.products)];
    };

    async function loadProducts() {
      setLoading(true);
      setError('');

      try {
        const hasMultipleSelections = selectedBrands.length > 1 || selectedCategories.length > 1;

        if (!hasMultipleSelections) {
          const response = await loadOnePage({
            brand: selectedBrands[0],
            category: selectedCategories[0],
            page: currentPage,
            limit: PRODUCTS_PER_PAGE
          });
          setResults(response.products);
          setTotalResults(response.totalResults);
          setTotalPages(response.totalPages);
        } else {
          const brands = selectedBrands.length ? selectedBrands : [null];
          const categories = selectedCategories.length ? selectedCategories : [null];
          const variants = brands.flatMap((brand) => categories.map((category) => ({ brand, category })));
          const responses = await Promise.all(
            variants.map(({ brand, category }) => loadVariant(brand, category))
          );

          const productMap = new Map();
          responses.flat().forEach((product) => {
            const key = product.idproducts || product.product_name;
            if (key) productMap.set(key, product);
          });

          const merged = sortProducts([...productMap.values()], selectedSort);
          const pages = Math.max(1, Math.ceil(merged.length / PRODUCTS_PER_PAGE));
          const safePage = Math.min(currentPage, pages);
          const start = (safePage - 1) * PRODUCTS_PER_PAGE;

          setResults(merged.slice(start, start + PRODUCTS_PER_PAGE));
          setTotalResults(merged.length);
          setTotalPages(pages);

          if (safePage !== currentPage) updateParams({ page: safePage });
        }
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          console.error('Unable to load search results:', requestError);
          setError('We could not load the catalog right now. Please try again.');
          setResults([]);
          setTotalResults(0);
          setTotalPages(1);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    loadProducts();
    return () => controller.abort();
  }, [
    searchTerm,
    location.search,
    selectedAvailability,
    selectedSort,
    appliedMinPrice,
    appliedMaxPrice,
    currentPage,
    selectedBrands,
    selectedCategories,
    updateParams
  ]);

  useEffect(() => {
    setQuantities((current) => {
      const next = { ...current };
      results.forEach((product) => {
        if (!next[product.idproducts]) next[product.idproducts] = 1;
      });
      return next;
    });
  }, [results]);

  const toggleMultiFilter = (key, currentValues, value) => {
    const nextValues = currentValues.includes(value)
      ? currentValues.filter((item) => item !== value)
      : [...currentValues, value];
    updateParams({ [key]: nextValues, page: 1 });
  };

  const clearAllFilters = () => {
    const next = new URLSearchParams();
    next.set('q', searchTerm);
    next.set('page', '1');
    navigate(`/search?${next.toString()}`);
    setMinPrice('');
    setMaxPrice('');
  };

  const applyPrice = () => {
    const min = minPrice === '' ? null : Number(minPrice);
    const max = maxPrice === '' ? null : Number(maxPrice);
    if ((min !== null && min < 0) || (max !== null && max < 0)) {
      toast.error('Prices cannot be negative.');
      return;
    }
    if (min !== null && max !== null && min > max) {
      toast.error('Minimum price cannot be greater than maximum price.');
      return;
    }
    updateParams({ minPrice: minPrice || null, maxPrice: maxPrice || null, page: 1 });
  };

  const addToCart = async (product) => {
    const quantity = quantities[product.idproducts] || 1;

    if (!user) {
      addToGuestCart({
        ...product,
        product_image: product.product_image?.replace(`${API_BASE}/data/`, ''),
        quantity
      });
      toast.success(`${truncateText(product.product_name, 30)} added to your cart.`);
      window.dispatchEvent(new Event('cartUpdated'));
      return;
    }

    const token = localStorage.getItem('user_token');
    if (!token) {
      navigate('/signup', { state: { returnTo: location.pathname + location.search } });
      return;
    }

    setAdding((current) => ({ ...current, [product.idproducts]: true }));
    try {
      const response = await fetch(`${API_BASE}/api/cart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          idusers: user.idusers,
          idproducts: Number.parseInt(product.idproducts, 10),
          product_name: product.product_name,
          product_description: product.product_description,
          product_price: product.product_price,
          product_image: product.product_image?.replace(`${API_BASE}/data/`, ''),
          product_brand: product.product_brand,
          quantity
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Could not add this product.');
      toast.success(`${truncateText(product.product_name, 30)} added to your cart.`);
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (requestError) {
      toast.error(requestError.message || 'Could not add this product.');
    } finally {
      setAdding((current) => ({ ...current, [product.idproducts]: false }));
    }
  };

  const activeFilters = [
    ...selectedBrands.map((value) => ({ type: 'brand', value })),
    ...selectedCategories.map((value) => ({ type: 'category', value })),
    ...(selectedAvailability ? [{ type: 'availability', value: selectedAvailability }] : [])
  ];

  const visibleBrands = showAllBrands ? facets.brands : facets.brands.slice(0, 8);
  const visibleCategories = showAllCategories ? facets.categories : facets.categories.slice(0, 8);

  const FilterSidebar = ({ availabilityGroupName }) => (
    <aside className="sr-sidebar" aria-label="Catalog filters">
      <div className="sr-sidebar-head">
        <div>
          <span>Refine results</span>
          <h2>Filters</h2>
        </div>
        {activeFilters.length > 0 || appliedMinPrice || appliedMaxPrice ? (
          <button type="button" onClick={clearAllFilters}>Clear all</button>
        ) : null}
      </div>

      <div className="sr-filter-group">
        <div className="sr-filter-title">
          <h3>Brands</h3>
          {selectedBrands.length > 0 && <span>{selectedBrands.length}</span>}
        </div>
        {facetsLoading ? (
          <div className="sr-filter-loading">Loading brands…</div>
        ) : visibleBrands.length > 0 ? visibleBrands.map((brand) => (
          <label className="sr-check-row" key={brand}>
            <input
              type="checkbox"
              checked={selectedBrands.includes(brand)}
              onChange={() => toggleMultiFilter('brand', selectedBrands, brand)}
            />
            <span className="sr-custom-check"><i className="fas fa-check" /></span>
            <span title={brand}>{brand}</span>
          </label>
        )) : <p className="sr-empty-filter">No brand options available.</p>}
        {facets.brands.length > 8 && (
          <button className="sr-show-more" type="button" onClick={() => setShowAllBrands((value) => !value)}>
            {showAllBrands ? 'Show less' : `Show ${facets.brands.length - 8} more`}
          </button>
        )}
      </div>

      <div className="sr-filter-group">
        <div className="sr-filter-title">
          <h3>Categories</h3>
          {selectedCategories.length > 0 && <span>{selectedCategories.length}</span>}
        </div>
        {facetsLoading ? (
          <div className="sr-filter-loading">Loading categories…</div>
        ) : visibleCategories.length > 0 ? visibleCategories.map((category) => (
          <label className="sr-check-row" key={category}>
            <input
              type="checkbox"
              checked={selectedCategories.includes(category)}
              onChange={() => toggleMultiFilter('category', selectedCategories, category)}
            />
            <span className="sr-custom-check"><i className="fas fa-check" /></span>
            <span title={category}>{category}</span>
          </label>
        )) : <p className="sr-empty-filter">No category options available.</p>}
        {facets.categories.length > 8 && (
          <button className="sr-show-more" type="button" onClick={() => setShowAllCategories((value) => !value)}>
            {showAllCategories ? 'Show less' : `Show ${facets.categories.length - 8} more`}
          </button>
        )}
      </div>

      <div className="sr-filter-group">
        <div className="sr-filter-title"><h3>Availability</h3></div>
        {[
          { value: 'in_stock', label: 'In stock' },
          { value: 'low_stock', label: 'Low stock' },
          { value: 'out_of_stock', label: 'Out of stock' }
        ].map((option) => {
          const isSelected = selectedAvailability === option.value;
          return (
            <label className={`sr-radio-row ${isSelected ? 'is-selected' : ''}`} key={option.value}>
              <input
                type="radio"
                name={availabilityGroupName}
                checked={isSelected}
                onChange={() => updateParams({
                  availability: isSelected ? null : option.value,
                  page: 1
                })}
              />
              <span className="sr-custom-radio" />
              <span>{option.label}</span>
            </label>
          );
        })}
        {selectedAvailability && (
          <button className="sr-show-more" type="button" onClick={() => updateParams({ availability: null, page: 1 })}>
            Any availability
          </button>
        )}
      </div>

      <div className="sr-filter-group">
        <div className="sr-filter-title"><h3>Price</h3></div>
        <div className="sr-price-row">
          <label><span>Min</span><input type="number" min="0" value={minPrice} onChange={(event) => setMinPrice(event.target.value)} placeholder="$0" /></label>
          <label><span>Max</span><input type="number" min="0" value={maxPrice} onChange={(event) => setMaxPrice(event.target.value)} placeholder="Any" /></label>
        </div>
        <button className="sr-apply-price" type="button" onClick={applyPrice}>Apply price</button>
      </div>

      <button className="sr-mobile-apply" type="button" onClick={() => setMobileFiltersOpen(false)}>
        View {totalResults} products
      </button>
    </aside>
  );

  return (
    <div className="sr-page">
      <Header />

      <main>
        <section className="sr-hero">
          <div className="sr-container sr-hero-inner">
            <div>
              <span className="sr-eyebrow">Industrial catalog</span>
              <h1>{searchTerm === '*' ? 'Explore all products' : `Results for “${searchTerm}”`}</h1>
              <p>Search by part number, product, brand or category, then refine the results for your project.</p>
            </div>
          </div>
        </section>

        <section className="sr-catalog">
          <div className="sr-container">
            <div className="sr-toolbar">
              <div>
                <button className="sr-mobile-filter-button" type="button" onClick={() => setMobileFiltersOpen(true)}>
                  <i className="fas fa-sliders-h" /> Filters
                  {activeFilters.length > 0 && <span>{activeFilters.length}</span>}
                </button>
                <p><strong>{totalResults.toLocaleString()}</strong> products found</p>
              </div>
              <label className="sr-sort">
                <span>Sort by</span>
                <select value={selectedSort} onChange={(event) => updateParams({ sort: event.target.value, page: 1 })}>
                  <option value="default">Recommended</option>
                  <option value="rank_asc">Most popular</option>
                  <option value="price_asc">Price: low to high</option>
                  <option value="price_desc">Price: high to low</option>
                  <option value="stock_desc">Stock: high to low</option>
                  <option value="brand_asc">Brand: A–Z</option>
                  <option value="name_asc">Name: A–Z</option>
                </select>
              </label>
            </div>

            {activeFilters.length > 0 || appliedMinPrice || appliedMaxPrice ? (
              <div className="sr-active-filters">
                <span>Active filters</span>
                {selectedBrands.map((brand) => (
                  <button type="button" key={`brand-${brand}`} onClick={() => toggleMultiFilter('brand', selectedBrands, brand)}>
                    {brand} <b>×</b>
                  </button>
                ))}
                {selectedCategories.map((category) => (
                  <button type="button" key={`category-${category}`} onClick={() => toggleMultiFilter('category', selectedCategories, category)}>
                    {category} <b>×</b>
                  </button>
                ))}
                {selectedAvailability && (
                  <button type="button" onClick={() => updateParams({ availability: null, page: 1 })}>
                    {selectedAvailability.replaceAll('_', ' ')} <b>×</b>
                  </button>
                )}
                {(appliedMinPrice || appliedMaxPrice) && (
                  <button type="button" onClick={() => updateParams({ minPrice: null, maxPrice: null, page: 1 })}>
                    ${appliedMinPrice || '0'}–{appliedMaxPrice ? `$${appliedMaxPrice}` : 'Any'} <b>×</b>
                  </button>
                )}
                <button className="sr-clear-inline" type="button" onClick={clearAllFilters}>Clear all</button>
              </div>
            ) : null}

            <div className="sr-layout">
              <FilterSidebar availabilityGroupName="availability-desktop" />

              <div className="sr-results">
                {loading ? (
                  <div className="sr-grid" aria-label="Loading products">
                    {Array.from({ length: 8 }, (_, index) => (
                      <div className="sr-card sr-skeleton" key={index}>
                        <div /><span /><span /><span />
                      </div>
                    ))}
                  </div>
                ) : error ? (
                  <div className="sr-message">
                    <span className="sr-message-icon"><i className="fas fa-exclamation-circle" /></span>
                    <h2>Catalog temporarily unavailable</h2>
                    <p>{error}</p>
                    <button type="button" onClick={() => window.location.reload()}>Try again</button>
                  </div>
                ) : results.length === 0 ? (
                  <div className="sr-message">
                    <img src={NotFoundImage} alt="" />
                    <h2>No products match these filters</h2>
                    <p>Remove one or more filters, or send the requirement to our sourcing team.</p>
                    <div>
                      <button type="button" onClick={clearAllFilters}>Clear filters</button>
                      <Link to="/about#contact">Ask our team</Link>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="sr-grid">
                      {results.map((product) => {
                        const stock = Number.parseInt(product.product_stock, 10) || 0;
                        const quantity = quantities[product.idproducts] || 1;
                        return (
                          <article className="sr-card" key={product.idproducts || product.product_name}>
                            <Link className="sr-card-image" to={getProductPath(product)}>
                              <span className={`sr-stock ${stock > 0 ? 'is-available' : 'is-unavailable'}`}>
                                <i /> {stock > 0 ? `${stock} in stock` : 'On request'}
                              </span>
                              <img
                                src={getProductImageUrl(product.product_image)}
                                alt={product.product_name}
                                loading="lazy"
                                onError={useProductImageFallback}
                              />
                            </Link>
                            <div className="sr-card-body">
                              <Link
                                className="sr-brand"
                                to={`/search?q=*&brand=${encodeURIComponent(product.product_brand || '')}&page=1`}
                              >
                                {truncateText(product.product_brand, 28)}
                              </Link>
                              <Link className="sr-product-name" to={getProductPath(product)}>
                                {product.product_name}
                              </Link>
                              <p>{truncateText(product.product_description, 92)}</p>
                              <div className="sr-card-price">
                                <strong>{formatPrice(product.product_price)}</strong>
                                <span>{stock > 0 ? 'Ready to ship' : 'Ask for lead time'}</span>
                              </div>
                              <div className="sr-card-actions">
                                <div className="sr-quantity">
                                  <button
                                    type="button"
                                    aria-label="Decrease quantity"
                                    disabled={quantity <= 1}
                                    onClick={() => setQuantities((current) => ({
                                      ...current,
                                      [product.idproducts]: Math.max(1, quantity - 1)
                                    }))}
                                  >−</button>
                                  <span>{quantity}</span>
                                  <button
                                    type="button"
                                    aria-label="Increase quantity"
                                    onClick={() => setQuantities((current) => ({
                                      ...current,
                                      [product.idproducts]: quantity + 1
                                    }))}
                                  >+</button>
                                </div>
                                <button
                                  className="sr-add-button"
                                  type="button"
                                  disabled={adding[product.idproducts]}
                                  onClick={() => addToCart(product)}
                                >
                                  {adding[product.idproducts] ? 'Adding…' : 'Add to cart'}
                                </button>
                              </div>
                            </div>
                          </article>
                        );
                      })}
                    </div>

                    {totalPages > 1 && (
                      <nav className="sr-pagination" aria-label="Product pages">
                        {currentPage > 1 ? (
                          <Link to={getPageUrl(currentPage - 1)} rel="prev">
                            <span>←</span> Previous
                          </Link>
                        ) : (
                          <span className="sr-page-disabled" aria-disabled="true"><span>←</span> Previous</span>
                        )}
                        <span>Page <strong>{currentPage}</strong> of {totalPages}</span>
                        {currentPage < totalPages ? (
                          <Link to={getPageUrl(currentPage + 1)} rel="next">
                            Next <span>→</span>
                          </Link>
                        ) : (
                          <span className="sr-page-disabled" aria-disabled="true">Next <span>→</span></span>
                        )}
                      </nav>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <div className={`sr-mobile-drawer ${mobileFiltersOpen ? 'is-open' : ''}`} aria-hidden={!mobileFiltersOpen}>
        <button className="sr-drawer-backdrop" type="button" aria-label="Close filters" onClick={() => setMobileFiltersOpen(false)} />
        <div className="sr-drawer-panel">
          <button className="sr-drawer-close" type="button" onClick={() => setMobileFiltersOpen(false)} aria-label="Close filters">×</button>
          <FilterSidebar availabilityGroupName="availability-mobile" />
        </div>
      </div>

      <Footer />
    </div>
  );
}
